import * as React from 'react';
import { useField, useFormikContext } from 'formik';
import Fieldset from '../../../commonComponents/fields/Fieldset';
import { isStripeCardDocumentType, isCompletedDocument } from '../../../core/domain';
import { hasValue, spacedWordsToTitleCase } from '../../../lib/utils';
import { AdditionalNameTypes, DbsTrustedGovernmentDocuments, NameChangeDocumentTypes } from '../../../core/enums';
import DocumentField from './DocumentField';
import { Component } from '../../../models/be/db/FormSpecification';
import Workflow from '../../../models/be/Workflow';
import { StripeCardDetails } from '../../../models/StripeCustomer';
import AdditionalName from '../../../models/be/AdditionalName';
import ErrorMessage from '../../../commonComponents/ErrorMessage';
import BirthDatesMatchValidator from './BirthDatesMatchValidator';
import { requiredErrorMessage, hiddenErrorKey } from './form';
import FullName, { hasSingleName, Name } from '../../../models/be/FullName';
import './NameChangeDocuments.less';

const PrimaryNameChangeDocuments = (props: Props) => {
	const [marriageDocumentFieldName, nameChangeDocumentFieldName] = props.fieldNames;

	const [, marriageFieldMeta, marriageFieldHelpers] = useField({ name: marriageDocumentFieldName });
	const [, nameChangeDocumentFieldMeta, nameChangeDocumentFieldHelpers] = useField({
		name: nameChangeDocumentFieldName
	});

	const { errors, setFieldError } = useFormikContext<{ [hiddenErrorKey]: string }>();
	const { marriageCertificateRequired, nameChangeDocumentRequired, hasMissingAdditionalName, misMatchedIdTypes } =
		hasNameChanged(props.visibleWorkflowFields);
	const shouldRender = marriageCertificateRequired || nameChangeDocumentRequired;

	if (
		marriageCertificateRequired &&
		!isCompletedDocument(marriageFieldMeta.value) &&
		!hasValue(marriageFieldMeta.error)
	) {
		marriageFieldHelpers.setError(requiredErrorMessage);
	}

	if (
		nameChangeDocumentRequired &&
		!isCompletedDocument(nameChangeDocumentFieldMeta.value) &&
		!hasValue(nameChangeDocumentFieldMeta.error)
	) {
		nameChangeDocumentFieldHelpers.setError(requiredErrorMessage);
	}

	const errorMessage = 'namesDontMatch';
	if (hasMissingAdditionalName && errors[hiddenErrorKey] !== errorMessage) {
		// Set the field after formik has finished rendering to avoid a React warning.
		setTimeout(() => {
			setFieldError(hiddenErrorKey, errorMessage);
		}, 0);
	}

	return (
		<>
			{shouldRender && (
				<Fieldset className="nameChangeDocument dataCaptureSection" title="Additional Name Change Documents">
					<div>
						{marriageCertificateRequired && (
							<div className="k-form-field">
								<DocumentField
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...props}
									workflowId={props.visibleWorkflowFields.id}
									candidateId={props.visibleWorkflowFields.candidateId}
									title="Marriage Certificate Image"
									documentTypes={[NameChangeDocumentTypes.MarriageCertificate]}
									fieldNames={[marriageDocumentFieldName]}
								/>
								<ErrorMessage message={marriageFieldMeta.error} />
							</div>
						)}
						{nameChangeDocumentRequired && (
							<div className="k-form-field">
								<DocumentField
									// eslint-disable-next-line react/jsx-props-no-spreading
									{...props}
									workflowId={props.visibleWorkflowFields.id}
									candidateId={props.visibleWorkflowFields.candidateId}
									title="Name Change Document Image"
									fieldNames={[nameChangeDocumentFieldName]}
									documentTypes={[NameChangeDocumentTypes.NameChangeDocument]}
								/>
								<ErrorMessage message={nameChangeDocumentFieldMeta.error} />
							</div>
						)}
					</div>
				</Fieldset>
			)}
			{errors[hiddenErrorKey] && (
				<ErrorMessage
					message={
						<p>
							ID must match your current name and birth date: {misMatchedIdTypes.map(spacedWordsToTitleCase).join(', ')}
							.
						</p>
					}
				/>
			)}
			<BirthDatesMatchValidator visibleWorkflowFields={props.visibleWorkflowFields} />
		</>
	);
};

export default PrimaryNameChangeDocuments;

interface Props extends Component {
	visibleWorkflowFields: Workflow;
	navigateToPage?: (pageSlug: string) => void;
	stripe?: stripe.Stripe;
}

PrimaryNameChangeDocuments.defaultProps = {
	navigateToPage: null,
	stripe: null
};

const hasNameChanged = (workflow: Workflow) => {
	const hasMarriageCertificate =
		workflow.identityDocuments?.firstSecondaryDocument?.documentType ===
		DbsTrustedGovernmentDocuments.UkMarriageCertificate;
	const { primaryName, additionalNames } = workflow;
	const maidenAdditionalName = getMaidenAdditionalName(additionalNames);
	const previousAndAliasAdditionalNames = getPreviousAndAliasAdditionalNames(additionalNames);
	const idTypeAndNames = getIdTypeAndNames(workflow);
	let marriageCertificateRequired = hasValue(maidenAdditionalName) && !hasMarriageCertificate;
	let nameChangeDocumentRequired = previousAndAliasAdditionalNames.length > 0;
	let hasMissingAdditionalName = false;
	const misMatchedIdTypes = [] as string[];

	idTypeAndNames.forEach((idTypeAndName) => {
		if (namesMatch(idTypeAndName, primaryName) && dobMatch(idTypeAndName, workflow.birthDetails.birthDate)) {
			// Do nothing.
		} else {
			hasMissingAdditionalName = true;
			misMatchedIdTypes.push(idTypeAndName.idType);
		}
	});

	const stripeCardNames = getAllUserDocumentsExceptNameChangeDocuments(workflow)
		.filter((d) => isStripeCardDocumentType(d?.documentType) && hasValue(d?.stripeCardDetails))
		.map((d) => splitCardName(d.stripeCardDetails));

	stripeCardNames.forEach((cardNames) => {
		if (hasValue(cardNames) && !doesCardNameMatch(cardNames, primaryName)) {
			if (doesCardNameMatch(cardNames, maidenAdditionalName)) marriageCertificateRequired = true;
			if (doesCardNameMatchAny(cardNames, previousAndAliasAdditionalNames)) nameChangeDocumentRequired = true;
			else {
				hasMissingAdditionalName = true;
				misMatchedIdTypes.push('bank/credit card');
			}
		}
	});

	return { marriageCertificateRequired, nameChangeDocumentRequired, hasMissingAdditionalName, misMatchedIdTypes };
};

const normalizeName = (name: string) => name?.replace(/[-' ]/g, '')?.toLowerCase() || '';

const namesMatch = (idTypeAndName: IdTypeAndName, otherName: FullName) => {
	const lastNamesMatch =
		(!hasValue(idTypeAndName.lastName) && !hasValue(otherName.lastName)) ||
		normalizeName(idTypeAndName.lastName) === normalizeName(otherName.lastName);

	if (idTypeAndName.idType === IdTypes.BirthCertificate) {
		const otherNameGivenNames = otherName.firstName;
		const givenNamesMatch = normalizeName(idTypeAndName.givenNames) === normalizeName(otherNameGivenNames);
		return givenNamesMatch && lastNamesMatch;
	}

	return normalizeName(idTypeAndName.firstName) === normalizeName(otherName.firstName) && lastNamesMatch;
};

const dobMatch = (idTypeAndName: IdTypeAndName, birthDate: string) => {
	return idTypeAndName.dob === birthDate;
};

const getMaidenAdditionalName = (additionalNames: AdditionalName[]) => {
	return additionalNames.find((x) => x.type === AdditionalNameTypes.Maiden);
};

const getPreviousAndAliasAdditionalNames = (additionalNames: AdditionalName[]) => {
	return additionalNames.filter((x) => x.type === AdditionalNameTypes.Alias || x.type === AdditionalNameTypes.Previous);
};

const getIdTypeAndNames = (workflow: Workflow): Set<IdTypeAndName> => {
	const { birthDetails, passportDetails, driverLicenceDetails, medicareDetails } = workflow;
	const idTypeAndNames = new Set<IdTypeAndName>();
	if (hasValue(birthDetails.givenNames)) {
		idTypeAndNames.add({
			idType: IdTypes.BirthCertificate,
			givenNames: birthDetails.givenNames,
			firstName: '',
			lastName: birthDetails.familyName ?? '',
			dob: birthDetails.birthDate
		});
	}
	if (hasValue(passportDetails.firstName)) {
		idTypeAndNames.add(createIdTypeAndName(IdTypes.Passport, passportDetails, passportDetails.birthDate));
	}
	if (workflow.workflowTypeCode !== 'RTW' && workflow.workflowTypeCode !== 'NZPV') {
		if (hasValue(driverLicenceDetails?.firstName)) {
			idTypeAndNames.add(
				createIdTypeAndName(
					IdTypes.DriverLicence,
					driverLicenceDetails,
					driverLicenceDetails?.birthDate ?? passportDetails.birthDate
				)
			);
		}
		if (hasValue(medicareDetails?.firstName)) {
			idTypeAndNames.add(createIdTypeAndName(IdTypes.Medicare, medicareDetails, passportDetails.birthDate));
		}
	}

	return idTypeAndNames;
};

const getAllUserDocumentsExceptNameChangeDocuments = (workflow: Workflow) => {
	return [
		workflow.passportDetails?.document,
		workflow.passportDetails?.passport,
		workflow.passportDetails?.visa,
		workflow.citizenshipCertificateDetails?.document,
		workflow.medicareDetails?.document,
		workflow.birthDetails?.document,
		workflow.driverLicenceDetails?.document,
		workflow.additionalDocuments?.documentOne,
		workflow.additionalDocuments?.documentTwo,
		workflow.additionalDocuments?.bankCardOrCreditCard,
		workflow.identityDocuments?.commencementDocument,
		workflow.identityDocuments?.primaryDocument,
		workflow.identityDocuments?.firstSecondaryDocument,
		workflow.identityDocuments?.secondSecondaryDocument,
		workflow.identityDocuments?.applicantAddressDocument,
		workflow.qualificationDetails?.degreeCertificate,
		workflow.qualificationDetails?.checkResult,
		workflow.pdfCertificate
	];
};

const splitCardName = (stripeCardDetails: StripeCardDetails) => {
	if (hasValue(stripeCardDetails?.cardName)) {
		return stripeCardDetails.cardName?.toLowerCase().split(' ');
	}
	return null;
};

const doesCardNameMatchAny = (cardNames: string[], knownNames: Name[]) => {
	return knownNames.length > 0 && knownNames.some((knownName) => doesCardNameMatch(cardNames, knownName));
};

const doesCardNameMatch = (cardNames: string[], knownName: Name) => {
	if (!hasValue(cardNames) || cardNames.length === 0 || !hasValue(knownName)) return false;
	if (hasSingleName(knownName)) {
		return cardNames.join(' ') === knownName.firstName?.toLowerCase();
	}
	const lastNameWords = knownName.lastName?.toLowerCase().split(' ');
	const doesLastNameMatch = lastNameWords?.every((lastNameWord) => cardNames.includes(lastNameWord));
	if (!doesLastNameMatch) return false;
	const doesFirstNameOrFirstNameInitialMatch =
		cardNames.includes(knownName.firstName?.toLowerCase()) || cardNames.includes(knownName.firstName[0]?.toLowerCase());
	return doesFirstNameOrFirstNameInitialMatch;
};

// eslint-disable-next-line no-shadow
enum IdTypes {
	BirthCertificate = 'birth certificate',
	Passport = 'passport',
	DriverLicence = 'driver licence',
	Medicare = 'medicare'
}

interface IdTypeAndName extends FullName {
	idType: IdTypes;
	givenNames?: string;
	dob?: string;
}

const createIdTypeAndName = (idType: IdTypes, fullName: FullName, dob: string): IdTypeAndName => {
	return {
		idType,
		firstName: fullName.firstName ?? '',
		lastName: fullName.lastName ?? '',
		dob
	};
};
